import { useState, useEffect, useLayoutEffect, useCallback, useRef, type RefObject } from 'react'

// Shared placement logic for portaled popover menus (DropdownSelect,
// SearchableSelect, the autocomplete inputs, the PhoneInput country picker).
//
// All of them portal a fixed-position panel next to a trigger. Left to their
// own devices they always open downwards from the trigger's bottom edge, so an
// instance low in the viewport spills off the bottom (and one near the right
// edge overflows sideways). This hook picks the side with room:
//   - opens below the trigger by default, flips above when it will not fit
//     below and there is more room above,
//   - left-aligns to the trigger, flips to right-aligned when that would
//     overflow the right edge,
//   - caps the scroll list to the space actually available on the chosen side.
//
// The panel may have non-scrolling chrome (a search header). Pass the outer
// panel as menuRef and the scrolling list as listRef; the hook measures the
// chrome as the difference so the flip decision uses the whole panel height
// while only the list is clamped.

interface AnchoredMenuArgs {
  // Whether the panel is currently mounted / shown.
  open: boolean
  anchorRef: RefObject<HTMLElement | null>
  menuRef: RefObject<HTMLElement | null>
  listRef: RefObject<HTMLElement | null>
  // Bump this whenever the list contents change (e.g. a search filter) so the
  // placement and height are recomputed against the new content.
  revision?: unknown
  // Gap between the trigger and the panel, in px.
  gap?: number
  // Keep the panel this many px away from the viewport edges.
  margin?: number
  // Hard cap for the scroll list height regardless of available space.
  maxListHeight?: number
  // Never shrink the list below this, even when cramped (it scrolls instead).
  minListHeight?: number
  // Floor for the panel width when it is wider than the trigger (PhoneInput).
  minMenuWidth?: number
}

export interface AnchoredMenuPos {
  top: number
  left: number
  // Trigger width, so callers can keep matching the panel to the trigger.
  width: number
  // Max height to apply to the scrolling list element.
  listMaxHeight: number
  placement: 'top' | 'bottom'
}

export function useAnchoredMenu({
  open,
  anchorRef,
  menuRef,
  listRef,
  revision,
  gap = 4,
  margin = 8,
  maxListHeight = 256,
  minListHeight = 96,
  minMenuWidth = 0,
}: AnchoredMenuArgs): AnchoredMenuPos {
  const [pos, setPos] = useState<AnchoredMenuPos>({
    top: 0,
    left: 0,
    width: 0,
    listMaxHeight: maxListHeight,
    placement: 'bottom',
  })
  // Sticky side for the lifetime of one open: a filtering list shrinks as you
  // type, so without this an upward-flipped panel could jump back below the
  // trigger mid-search. Reset to null on close (see the layout effect).
  const stickyPlacement = useRef<'top' | 'bottom' | null>(null)

  const recompute = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const r = anchor.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    const menu = menuRef.current
    const list = listRef.current
    // Non-scrolling part of the panel (search header + borders). offsetHeight of
    // the list reflects its clamped height, scrollHeight its full content, so
    // chrome stays stable no matter how the list is currently sized.
    const chrome = menu && list ? Math.max(0, menu.offsetHeight - list.offsetHeight) : 0
    const contentH = list ? list.scrollHeight : maxListHeight
    const desiredList = Math.min(contentH, maxListHeight)
    const desiredMenu = chrome + desiredList

    const spaceBelow = vh - r.bottom - gap - margin
    const spaceAbove = r.top - gap - margin
    const natural = spaceBelow < desiredMenu && spaceAbove > spaceBelow ? 'top' : 'bottom'
    // Keep an already-opened upward panel upward while there is still room above.
    const placement =
      stickyPlacement.current === 'top' && spaceAbove >= minListHeight ? 'top' : natural
    stickyPlacement.current = placement

    const availMenu = placement === 'top' ? spaceAbove : spaceBelow
    const listMaxHeight = Math.max(minListHeight, Math.min(desiredList, availMenu - chrome))
    const renderedMenu = chrome + Math.min(desiredList, listMaxHeight)
    const top = placement === 'top' ? r.top - gap - renderedMenu : r.bottom + gap

    const menuW = Math.max(r.width, minMenuWidth, menu ? menu.offsetWidth : 0)
    let left = r.left
    if (left + menuW > vw - margin) left = r.right - menuW
    if (left < margin) left = margin

    setPos({ top, left, width: r.width, listMaxHeight, placement })
  }, [anchorRef, menuRef, listRef, gap, margin, maxListHeight, minListHeight, minMenuWidth])

  // Position before paint so the panel never flashes at its pre-measured spot.
  // Re-runs when the content revision changes so a filtered list re-picks a side.
  useLayoutEffect(() => {
    if (open) recompute()
    else stickyPlacement.current = null
  }, [open, revision, recompute])

  // Keep it anchored while the page or any ancestor scrolls / resizes.
  useEffect(() => {
    if (!open) return
    const onChange = () => recompute()
    window.addEventListener('scroll', onChange, true)
    window.addEventListener('resize', onChange)
    return () => {
      window.removeEventListener('scroll', onChange, true)
      window.removeEventListener('resize', onChange)
    }
  }, [open, recompute])

  return pos
}
